import clsx from 'clsx'
import React, {useMemo} from 'react'
import {useIntl} from 'react-intl'
import {useLocation} from 'react-router'
import {CSSTransition} from 'react-transition-group'
import {KTSVG, checkIsActive, checkIsActiveSubRoute} from '../../../../_gori/helpers'
import {Link} from 'react-router-dom'
import UseRoutesPermission from '../../../../_gori/hooks/UseRoutesPermission'
import {ROUTES, USER_STATUS} from '../../../../_gori/constants'
import {isEnterpriseOwner, useAuth} from '../../auth'

const SettingsMenu: React.FC = () => {
  const intl = useIntl()
  const {pathname} = useLocation()
  const {routes} = UseRoutesPermission()
  const {currentUser} = useAuth()

  const menuTab = useMemo(() => {
    const tabs = [
      {
        label: 'ACCOUNT',
        sub_route: [
          ROUTES.SETTINGS.PROFILE,
          ROUTES.SETTINGS.GENERAL,
          ROUTES.SETTINGS.PAYMENT_PLAN,
          ROUTES.SETTINGS.USER_MANAGEMENT,
          ROUTES.SETTINGS.API_KEYS,
          ROUTES.SETTINGS.SECURITY,
        ],
        sub_menu: [
          {label: 'PROFILE', path: ROUTES.SETTINGS.PROFILE},
          ...(routes.ADMINS.hasPermission
            ? [{label: 'GENERAL', path: ROUTES.SETTINGS.GENERAL}]
            : []),
          ...(routes.ADMINS.hasPermission
            ? [{label: 'PAYMENT_PLAN', path: ROUTES.SETTINGS.PAYMENT_PLAN}]
            : []),
          ...(routes.USERS_VIEW.hasPermission
            ? [
                {
                  label: 'USER_MANAGEMENT',
                  path: `${ROUTES.SETTINGS.USER_MANAGEMENT}?status=${USER_STATUS.ACTIVE}`,
                },
              ]
            : []),
          ...(routes.ADMINS.hasPermission
            ? [{label: 'API_KEYS', path: ROUTES.SETTINGS.API_KEYS}]
            : []),
          {label: 'SECURITY', path: ROUTES.SETTINGS.SECURITY},
        ],
      },
    ]

    const shippingMenu = [
      ...(routes.ADMINS.hasPermission
        ? [{label: 'CARRIERS', path: ROUTES.SETTINGS.CARRIER_ROUTE}]
        : []),
      ...(routes.SETTINGS_ADDRESSES.hasPermission
        ? [{label: 'SHIPPING_ADDRESSES', path: ROUTES.SETTINGS.SHIPPING_ADDRESSES}]
        : []),
      ...(routes.SETTINGS_PACKAGES.hasPermission
        ? [{label: 'PACKAGES', path: ROUTES.SETTINGS.PACKAGES}]
        : []),
      ...(routes.SETTINGS_PRESETS.hasPermission
        ? [{label: 'PRESETS', path: ROUTES.SETTINGS.PRESETS}]
        : []),
      ...(routes.SETTINGS_LABELS_SLIPS.hasPermission
        ? [{label: 'LABELS_AND_PACKING_SLIPS', path: ROUTES.SETTINGS.LABELS_SLIPS}]
        : []),
      ...(routes.SETTINGS_INSURANCE.hasPermission
        ? [{label: 'INSURANCE', path: ROUTES.SETTINGS.INSURANCE}]
        : []),
      ...(routes.AUTOMATION_RULES.hasPermission
        ? [{label: 'AUTOMATION_RULES', path: ROUTES.SETTINGS.AUTOMATION_RULES}]
        : []),
    ]

    if (shippingMenu.length > 0) {
      tabs.push({
        label: 'SHIPPING',
        sub_route: shippingMenu.map((item) => item.path),
        sub_menu: shippingMenu,
      })
    }

    const storeSetUpMenu = [
      ...(routes.ADMINS.hasPermission
        ? [{label: 'STORE_CONNECTIONS', path: ROUTES.SETTINGS.CONNECTIONS.BASE}]
        : []),
    ]

    if (storeSetUpMenu.length > 0) {
      tabs.push({
        label: 'STORE_SETUP',
        sub_route: [ROUTES.SETTINGS.CONNECTIONS.BASE],
        sub_menu: storeSetUpMenu,
      })
    }

    const organizationMenu = isEnterpriseOwner(currentUser)
      ? [
          ...(routes.SETTINGS_MANAGERS.hasPermission
            ? [{label: 'MANAGERS', path: ROUTES.SETTINGS.MANAGERS}]
            : []),
          ...(routes.SETTINGS_CUSTOMER_INVITE.hasPermission
            ? [{label: 'CUSTOMER_INVITE', path: ROUTES.SETTINGS.CUSTOMER_INVITE}]
            : []),
        ]
      : []

    if (organizationMenu.length > 0) {
      tabs.push({
        label: 'ORGANIZATION',
        sub_route: organizationMenu.map((item) => item.path),
        sub_menu: organizationMenu,
      })
    }

    return tabs
  }, [routes, currentUser])

  return (
    <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
      <div className='d-flex flex-column gap-4'>
        {Object.entries(menuTab).map(([, value], index) => {
          return (
            <div key={index} className='settings-menu__section mb-0'>
              <div
                className={clsx(
                  'settings-menu__heading rounded-2 px-5 py-2 fw-bolder fs-6 bg-light-secondary',
                  {
                    'active bg-light-primary': checkIsActiveSubRoute(value.sub_route, pathname),
                  }
                )}
              >
                {intl.formatMessage({id: value.label})}
              </div>
              <div className='d-flex flex-column settings-menu__links'>
                {value.sub_menu.map((route, index) => {
                  return (
                    <Link
                      key={index}
                      to={route.path}
                      className={clsx(
                        'dropdown-item settings-menu__link d-flex align-items-center px-5 py-2 fs-7 fw-bolder',
                        {
                          active: checkIsActive(pathname, route.path),
                          'bg-hover-white text-hover-gray-900': !checkIsActive(
                            pathname,
                            route.path
                          ),
                        }
                      )}
                    >
                      <KTSVG
                        path='/media/gori/arrows/arrow_right.svg'
                        className={clsx('settings-menu__icon me-2', {
                          'text-gray-700': !checkIsActive(pathname, route.path),
                          'text-primary': checkIsActive(pathname, route.path),
                        })}
                      />
                      {intl.formatMessage({id: route.label})}
                    </Link>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </CSSTransition>
  )
}

export {SettingsMenu}
